// 📂 Datei: GPT_TerminalConsole.js – v2.3 final integration (kombiniert)

import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../config/config";

const TerminalConsole = () => {
  const [input, setInput] = useState("");
  const [history, setHistory] = useState(() => {
    const stored = localStorage.getItem("c3po_history");
    return stored ? JSON.parse(stored) : [];
  });
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [stage, setStage] = useState("login");
  const [username, setUsername] = useState("");
  const [lastInteraction, setLastInteraction] = useState(Date.now());
  const scrollRef = useRef(null);
  const timeoutMinutes = 30;

  useEffect(() => {
    const interval = setInterval(() => {
      if (isAuthenticated && Date.now() - lastInteraction > timeoutMinutes * 60000) {
        setHistory((prev) => [...prev, "⌛ Sitzung abgelaufen (Timeout)."]);
        resetSession();
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, lastInteraction]);

  useEffect(() => {
    localStorage.setItem("c3po_history", JSON.stringify(history));
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const resetSession = () => {
    setIsAuthenticated(false);
    setStage("login");
    setUsername("");
    setHistory([]);
    localStorage.removeItem("c3po_history");
  };

  const handleCommand = async (command) => {
    setLastInteraction(Date.now());
    setHistory((prev) => [...prev, `> ${command}`]);
    setLoading(true);
    try {
      const response = await axios.post(`${config.API_BASE_URL}/api/gpt/c3po/task`, {
        prompt: command,
      });
      const output = renderOutput(response.data?.output || response.data);
      setHistory((prev) => [...prev, output]);
    } catch (err) {
      setHistory((prev) => [...prev, "❌ Fehler bei C-3PO: " + err.message]);
    } finally {
      setLoading(false);
    }
  };

  const renderOutput = (data) => {
    if (typeof data === "string") {
      return (
        <div
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            maxWidth: "100%",
            fontFamily: "monospace",
            lineHeight: "1.5"
          }}
        >
          {data}
        </div>
      );
    }

    if (React.isValidElement(data)) return data;

    try {
      if (typeof data === "object") {
        if (data.type === "directory" && Array.isArray(data.contents)) {
          return (
            <div>
              <pre className="text-green-400 mb-2">📁 Inhalt von {data.path}</pre>
              <ul className="pl-4">
                {data.contents.map((item, idx) => (
                  <li key={idx}>
                    <button
                      className="text-green-300 hover:text-green-100 underline"
                      onClick={() =>
                        handleCommand(`zeig mir ${data.path.replace(/\/$/, "")}/${item}`)
                      }
                    >
                      • {item}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        }
        return (
          <pre className="overflow-auto whitespace-pre-wrap break-words">
            {JSON.stringify(data, null, 2)}
          </pre>
        );
      }
      return <pre>{String(data)}</pre>;
    } catch (err) {
      return <pre className="text-red-500">⚠️ Fehler bei der Anzeige: {String(err.message)}</pre>;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) return;

    if (!isAuthenticated) {
      if (stage === "login") {
        setUsername(trimmed);
        setStage("password");
        setInput("");
        return;
      }
      if (stage === "password") {
        if (trimmed === config.C3PO_KEY) {
          setIsAuthenticated(true);
          setStage("ready");
          setHistory((prev) => [...prev, `✅ Willkommen, ${username}`]);
        } else {
          setHistory((prev) => [...prev, "❌ Falsches Passwort."]);
          resetSession();
        }
        setInput("");
        return;
      }
    }

    if (trimmed.toLowerCase() === "logout") {
      setHistory((prev) => [...prev, "👋 Du hast dich abgemeldet."]);
      resetSession();
      return;
    }

    await handleCommand(trimmed);
    setInput("");
  };

  const getPrompt = () =>
    !isAuthenticated ? (stage === "login" ? "Login:" : "Passwort:") : `C-3PO: `;

  return (
    <div className="min-h-screen p-6 text-green-400 font-mono bg-black">
      <div className="bg-black bg-opacity-90 p-4 rounded-xl shadow-inner max-w-6xl mx-auto">
        <h2 className="text-green-400 text-xl font-bold mb-2">C-3PO Terminal</h2>
        <div
          ref={scrollRef}
          className="border border-green-500 p-4 rounded overflow-y-auto mb-2 bg-black"
          style={{ height: "calc(100vh - 270px)" }}
        >
          {history.map((line, idx) => (
            <div
              key={idx}
              className="mb-2 whitespace-pre-wrap break-words"
            >
              {React.isValidElement(line) ? line : (
                <div
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    maxWidth: "100%",
                    lineHeight: "1.4",
                    fontFamily: "monospace"
                  }}
                >
                  {String(line)}
                </div>
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex items-center space-x-2">
          <span className="text-green-300">{getPrompt()}</span>
          <input
            type={stage === "password" ? "password" : "text"}
            autoFocus
            className="flex-1 bg-black text-green-400 border-none outline-none font-mono"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
        </form>

        <div className="mt-4 text-right">
          <Link to="/dashboard">
            <button className="bg-green-800 hover:bg-green-700 text-white px-4 py-2 rounded">
              ⬅ Zurück zum Dashboard
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TerminalConsole;